import { createContext, useContext, useEffect, ReactNode } from 'react'
import { COLORS } from '@/constants/colors'

interface ThemeContextType {
  colors: typeof COLORS
  fontFamily: string
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Single, fixed theme: modern
  const colors = COLORS
  const fontFamily = "'TWKEverett', sans-serif"

  useEffect(() => {
    document.body.style.fontFamily = fontFamily
    document.documentElement.style.setProperty(
      '--theme-font-family',
      fontFamily
    )
    document.documentElement.style.setProperty(
      '--theme-primary',
      colors.primary
    )
    document.documentElement.style.setProperty(
      '--theme-background',
      colors.background
    )
    document.documentElement.style.setProperty('--theme-yellow', colors.yellow)

    // Detect MetaMask browser and add class to body
    const win = window as Window & {
      opera?: unknown
      ethereum?: { isMetaMask?: boolean }
    }
    const operaUA = typeof win.opera === 'string' ? win.opera : ''
    const userAgent = navigator.userAgent || navigator.vendor || operaUA || ''
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)
    const isMetaMask = Boolean(win.ethereum?.isMetaMask)
    const isMetaMaskBrowser = isMetaMask && isMobile
    if (isMetaMaskBrowser) {
      document.body.classList.add('metamask-browser')
    } else {
      document.body.classList.remove('metamask-browser')
    }

    return () => {
      document.body.style.fontFamily = "'TWKEverett', sans-serif"
      document.documentElement.style.setProperty(
        '--theme-font-family',
        "'TWKEverett', sans-serif"
      )
      document.documentElement.style.setProperty(
        '--theme-primary',
        COLORS.primary
      )
      document.documentElement.style.setProperty(
        '--theme-background',
        COLORS.background
      )
      document.documentElement.style.setProperty(
        '--theme-yellow',
        COLORS.yellow
      )
      document.body.classList.remove('metamask-browser')
    }
  }, [fontFamily, colors.background, colors.primary, colors.yellow])

  return (
    <ThemeContext.Provider value={{ colors, fontFamily }}>
      {children}
    </ThemeContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
